/**
 * What the Log sheet offers first on a travel day.
 *
 * Every transport and every airport at once is too much to scan, so the
 * sheet shows a short list of what fits the moment and keeps the rest behind
 * "More":
 *
 *   - Airports: only the ones that fit right now. The airport a logged flight
 *     lands at (or leaves from) that you haven't logged yet comes first; then
 *     an airport you're near. Every other airport waits in More.
 *   - Ways of getting about, most used first. One that belongs to a place
 *     (the Monorail under Walt Disney World) only shows on a trip that goes
 *     there; elsewhere it waits in More.
 *   - Retired places (with an end date) never make the short list; search
 *     still finds them.
 */

import type { EntityType } from "../types";
import { flightRoute } from "./flightRoute";

export interface TravelPlace {
  id: string;
  displayName: string;
  type: EntityType;
  parentId?: string | null;
  activeTo?: unknown;
}

export interface TravelPicks<T extends TravelPlace> {
  /** Airports from this day's flights not yet visited, in flight order. */
  fromFlights: T[];
  /** Airports you're near, nearest first. */
  nearbyAirports: T[];
  /** The ways of getting about that fit this trip, most used first. */
  transport: T[];
  /** Everything else, for "More". */
  more: T[];
}

export function travelPicks<T extends TravelPlace>(
  places: T[],
  opts: {
    /** How often each place has been logged, by id. */
    usage: Record<string, number>;
    /** The travel day's activities so far. */
    dayActivities: { entityId?: string | null; customResponses?: Record<string, unknown> | null }[];
    /** Places this trip goes to, with their parents (parks, resorts, destinations). */
    tripPlaceIds: Set<string>;
    /** Airports near you, nearest first. */
    nearbyAirportIds?: string[];
    transportLimit?: number;
  },
): TravelPicks<T> {
  const { usage, dayActivities, tripPlaceIds, nearbyAirportIds = [], transportLimit = 5 } = opts;
  const byId = new Map(places.map((p) => [p.id, p] as const));
  const visited = new Set(dayActivities.map((a) => a.entityId).filter((id): id is string => !!id));
  const live = (p: T) => p.activeTo == null;

  const fromFlights: T[] = [];
  for (const a of dayActivities) {
    const { from, to } = flightRoute(a.customResponses);
    for (const id of [from, to]) {
      const p = id ? byId.get(id) : undefined;
      if (p && live(p) && !visited.has(p.id) && !fromFlights.includes(p)) fromFlights.push(p);
    }
  }

  const nearbyAirports = nearbyAirportIds
    .map((id) => byId.get(id))
    .filter((p): p is T => !!p && p.type === "AIRPORT" && live(p) && !fromFlights.includes(p))
    .slice(0, 2);

  const byUse = (a: T, b: T) =>
    (usage[b.id] ?? 0) - (usage[a.id] ?? 0) || a.displayName.localeCompare(b.displayName);
  const fitsTrip = (p: T) => !p.parentId || tripPlaceIds.has(p.parentId);
  const transport = places
    .filter((p) => p.type !== "AIRPORT" && live(p) && fitsTrip(p))
    .sort(byUse)
    .slice(0, transportLimit);

  const shown = new Set<T>([...fromFlights, ...nearbyAirports, ...transport]);
  const more = places
    .filter((p) => !shown.has(p))
    .sort(
      (a, b) =>
        (a.type === "AIRPORT" ? 1 : 0) - (b.type === "AIRPORT" ? 1 : 0) ||
        a.displayName.localeCompare(b.displayName),
    );

  return { fromFlights, nearbyAirports, transport, more };
}
